import { Avatar, Box, Button, Skeleton, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { UserMenuNavbar } from './UserMenuNavbar';
import { useMe } from '@/features/auth';
import { ArrowDropDown } from '@mui/icons-material';

const UserNavbar = () => {
    const [prfName, setPrfName] = useState('');
    const { data: meResponse, isLoading: loading, error: userError } = useMe();
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const userData = meResponse?.data?.success ? meResponse.data.data : null;
    const { name, email } = userData || {};
    const error = userError ? 'Failed to load user data' : null;

    useEffect(() => {
        if (name) {
            setPrfName(name);
        } else if (email) {
            setPrfName(email.split('@')[0]); // Use email username part if name is not available
        } else {
            setPrfName('');
        }
    }, [name, email]);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <Box className="flex items-center gap-x-3 sm:gap-x-4">
            <Box>
                {loading ? (
                    <Box className="flex items-center gap-x-3">
                        <Skeleton variant="circular" width={40} height={40} />
                    </Box>
                ) : (
                    <Button
                        onClick={handleClick}
                        size="large"
                        className="normal-case"
                        aria-controls={open ? 'account-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? 'true' : undefined}
                        endIcon={
                            <Avatar
                                sx={{
                                    width: 40,
                                    height: 40,
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                    color: 'var(--color-primary)',
                                    backgroundColor: 'var(--color-sidebar-background-activated)',
                                    border: 'none',
                                    boxShadow: 'var(--shadow-primary-soft)',
                                }}
                            >
                                {error ? '!' : prfName.charAt(0).toUpperCase()}
                            </Avatar>
                        }
                        startIcon={
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    transition: 'transform 0.2s ease',
                                    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                                    '& svg': {
                                        fill: 'var(--color-text-disabled)',
                                    },
                                }}
                            >
                                <ArrowDropDown className="" />
                            </Box>
                        }
                        sx={{
                            color: 'var(--color-text-dark)',
                            fontWeight: 600,
                            px: 2,
                            py: 1,
                            borderRadius: 2.5,
                            textTransform: 'none',
                            backgroundColor: open ? 'rgba(0, 0, 0, 0.03)' : 'transparent',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                transform: 'translateY(-1px)',
                                boxShadow: 'var(--shadow-subtle)',
                            },
                            '& .MuiButton-startIcon': {
                                marginRight: 1.5,
                            },
                            '& .MuiButton-endIcon': {
                                marginLeft: 1.5,
                            },
                        }}
                    >
                        <Box className="flex flex-col items-start min-w-0">
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    lineHeight: 1.3,
                                    fontSize: '0.938rem',
                                    color: 'var(--color-text-dark)',
                                    letterSpacing: '-0.01em',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '100%',
                                }}
                            >
                                {error ? error : prfName}
                            </Typography>
                        </Box>
                    </Button>
                )}

                <UserMenuNavbar anchorEl={anchorEl} open={open} handleClose={handleClose} prfName={prfName} email={email} />
            </Box>
        </Box>
    );
};

export default UserNavbar;
